import React from 'react';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import { WithStyles } from '@material-ui/core/styles';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink, match } from 'react-router-dom';
// Components
import { Anchor } from '../../../components/anchor';
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedListItem from '../../../components/listItem';
import { FormatType, ListCell } from '../../../local-types';
import { InlineDocument } from '../../../components/inlineDocument';
// Modules
import { Vehicle, VehicleById, DocumentType, TParams } from '../../../redux/reducers/api-types';
import { selectors as documentSelectors } from '../../../redux/reducers/documents';
import { actions as errorActions, ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as vehicleActions,
    selectors as vehicleSelectors,
    types as vehicleTypes,
} from '../../../redux/reducers/vehicles';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { createPath, paths } from '../../paths';
import ActionLogs from '../../../components/action-logs/action-logs';
import { getVehicleDetails, getVehicleModel } from '../../../utils/businessUtils';
import { clsx } from '../../../utils/htmlUtils';

interface Props extends WithTranslation, WithStyles {
    globalClasses: any;
    match: match<TParams>;
    vehicles: VehicleById;
    documents: any;
    getVehicleError: ErrorPayload;
    clearError: (errorType: string) => void;
    setVehicleEnableError: ErrorPayload;
    getVehicle: (id: string) => any;
    setEnable: (id: string, enable: boolean, payload: { id: string }) => void;
}

class VehicleViewPage extends React.Component<Props> {
    canWriteSettings: boolean = UserUtils.checkRights({
        name: AccountRight.PARTNER_SETTINGS,
        mode: RightRank.W,
    });

    componentDidMount() {
        this.load();
    }

    load = (): void => {
        // A failure recorded for the entity we come from would otherwise greet the next
        // one with the retry panel instead of the loader.
        this.props.clearError(vehicleTypes.GET_VEHICLE);

        const { match, getVehicle } = this.props;

        const id = match.params.id;
        getVehicle(id);
    };

    /**
     * Every inactive vehicle used to read as "Suspendu" whatever put it out of service -
     * a partner deactivation, an expired document and an admin sanction were
     * indistinguishable. deletion_reason carries the real cause.
     */
    deletionReasonLabel = (vehiclePayload: Vehicle): string => {
        const { t } = this.props;
        const reason = vehiclePayload?.deletion_reason;
        if (!reason) {
            return t('deletion_reasons.unknown');
        }
        // Unmapped Vehicle::DELETED_* values surface raw rather than silently vanish.
        return t(`deletion_reasons.${reason}`, { defaultValue: reason });
    };

    handleAction = (activate: boolean) => (e) => {
        const { match } = this.props;
        const id = match.params.id;
        this.props.setEnable(id, activate, { id });
    };

    listItemsBuilder = (vehiclePayload: Vehicle, documents): ListCell[] => {
        const { t } = this.props;
        if (!vehiclePayload) {
            return [];
        }

        const model = getVehicleModel(vehiclePayload);

        const listItem: ListCell[] = [
            { title: t('common:form.id'), data: vehiclePayload.id, readOnly: true },
            {
                title: t('model'),
                data: model,
                // No referrer, so Google never learns the back office URL we come from
                customValue: model && (
                    <Link
                        color="secondary"
                        href={`https://www.google.com/search?q=${encodeURIComponent(model)}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        referrerPolicy="no-referrer"
                    >
                        {model}
                    </Link>
                ),
            },
            {
                title: t('registration_number'),
                data: vehiclePayload.registration_number,
            },
            {
                title: t('color'),
                data: vehiclePayload.vehicle_color?.name,
            },
            {
                title: t('type'),
                data: vehiclePayload.vehicle_model?.type.name,
            },
            // {
            //     title: t('dispatch_group'),
            //     data:
            //         vehiclePayload.dispatch_groups &&
            //         vehiclePayload.dispatch_groups.map(({ id, name }) => <div key={id}>{name}</div>),
            //     readOnly: true,
            // },
            {
                title: t('common:form.partner'),
                data: vehiclePayload.partner && (
                    <Link
                        color="secondary"
                        component={RouterLink}
                        to={`${paths.partners}/${vehiclePayload.partner.id}`}
                    >
                        {vehiclePayload.partner.name}
                    </Link>
                ),
                readOnly: true,
            },
            {
                title: t('creation_date'),
                data: vehiclePayload.creation_date,
                type: FormatType.DateShort,
                readOnly: true,
            },
            {
                title: t('deletion_date'),
                data: vehiclePayload.deletion_date,
                type: FormatType.DateShort,
                optional: true,
                readOnly: true,
            },
            {
                title: t('deletion_reason'),
                data: vehiclePayload.deletion_date ? this.deletionReasonLabel(vehiclePayload) : null,
                optional: true,
                readOnly: true,
            },
            {
                title: t('immatriculation_date'),
                data:
                    (documents && documents[vehiclePayload.documents?.grey_card]?.current_version?.start_date) ||
                    vehiclePayload.immatriculation_date,
                type: FormatType.DateShort,
                readOnly: true,
            },
            {
                title: t('insurance_validity_date'),
                data:
                    (documents && documents[vehiclePayload.documents?.green_card]?.current_version?.end_date) ||
                    vehiclePayload.insurance_validity_date,
                type: FormatType.DateShort,
                readOnly: true,
            },
            {
                title: t('motorization'),
                data: vehiclePayload.vehicle_motorization?.localized_string?.fr,
            },
        ];
        return listItem;
    };

    generateDocuments = (vehiclePayload: Vehicle): DocumentType[] => {
        const { t, documents } = this.props;

        if (!vehiclePayload) {
            return [];
        }

        const documentsType: DocumentType[] = [
            {
                id: vehiclePayload.documents.insurance as any,
                name: t('common:docs.insurance'),
                docType: 'insurance',
                endDate: true,
            },
            {
                id: vehiclePayload.documents.green_card,
                name: t('common:docs.green_card'),
                docType: 'green_card',
                endDate: true,
            },
            {
                id: vehiclePayload.documents.grey_card,
                name: t('common:docs.grey_card'),
                docType: 'grey_card',
                endDate: !!vehiclePayload.documents.grey_card,
                endDateDisable: true,
                startDate: true,
                startLabel: t('date_grey_card'),
            },
            {
                id: vehiclePayload.documents.technical_control,
                name: t('common:docs.technical_control'),
                docType: 'technical_control',
                endDate:
                    !!vehiclePayload.documents.technical_control &&
                    !!documents[vehiclePayload.documents.technical_control]?.current_version,
                endDateDisable: true,
                startDate: true,
                startLabel: t('date_technical_control'),
            },
        ];
        return documentsType;
    };

    generateTitle = (vehiclePayload: Vehicle) => {
        return getVehicleDetails(vehiclePayload);
    };

    //======================================//
    //=============== Render ===============//
    //======================================//

    generateListDocuments = (vehiclePayload: Vehicle): ListCell[] => {
        const { t } = this.props;
        const documents = this.generateDocuments(vehiclePayload);

        const cells: ListCell[] = documents.map((doc) => {
            return {
                title: t(`common:docs.${doc.docType}`),
                data: true,
                customValue: (
                    <InlineDocument
                        {...doc}
                        docClass="vehicle"
                        readOnly={!this.canWriteSettings}
                        classId={vehiclePayload.id}
                        noLabel={true}
                    />
                ),
            };
        });

        return cells;
    };

    generateAppHeader = (vehiclePayload: Vehicle): any => {
        const { t } = this.props;
        return {
            icon: null,
            title: this.generateTitle(vehiclePayload),
            subtitle: vehiclePayload && `ID: ${vehiclePayload.id}`,
            bubble: {
                visible: vehiclePayload?.deletion_date,
                text: t('common:suspended'),
                // The pill has to stay short; the reason rides in its tooltip and is
                // spelled out in full in the general infos block.
                tooltip: vehiclePayload?.deletion_date ? this.deletionReasonLabel(vehiclePayload) : '',
            },
            anchors: [
                { href: '#general_infos', label: t('common:form.general_infos') },
                { href: '#documents', label: t('common:form.documents') },
            ],
        };
    };

    render() {
        const { t, globalClasses, match, vehicles, documents, getVehicleError, setVehicleEnableError } = this.props;
        const id = match.params.id;
        const vehiclePayload = vehicles[id];

        return (
            <App
                needRights={{ name: AccountRight.PARTNER_SETTINGS, mode: RightRank.R }}
                loading={!vehiclePayload}
                loadingError={this.props.getVehicleError}
                onRetry={this.load}
                header={this.generateAppHeader(vehiclePayload)}
            >
                <ErrorMessage payload={getVehicleError} />
                {vehiclePayload && (
                    <>
                        <Anchor id="general_infos" />
                        <EnhancedListItem
                            datas={this.listItemsBuilder(vehiclePayload, documents)}
                            title={t('common:form.general_infos')}
                            cardActions={
                                this.canWriteSettings && (
                                    <>
                                        <ErrorMessage payload={setVehicleEnableError} />
                                        {vehiclePayload.deletion_date ? (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="secondary"
                                                onClick={this.handleAction(true)}
                                            >
                                                {t('enable')}
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                onClick={this.handleAction(false)}
                                                className={clsx(globalClasses.btnDanger, globalClasses.btnAutoWidth)}
                                            >
                                                {t('disable')}
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            color="secondary"
                                            variant="contained"
                                            component={RouterLink}
                                            to={createPath(paths.vehiclesEdit, { id })}
                                        >
                                            {t('common:edit')}
                                        </Button>
                                    </>
                                )
                            }
                        />
                        <br />
                        <Anchor id="documents" />
                        <EnhancedListItem
                            datas={this.generateListDocuments(vehiclePayload)}
                            title={t('common:form.documents')}
                        />
                        <ActionLogs history={vehiclePayload.history} withValues />
                    </>
                )}
            </App>
        );
    }
}

const VehicleViewPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['vehicle', 'common'])(VehicleViewPage),
);

const mapStateToProps = (state) => ({
    vehicles: vehicleSelectors.vehicles(state),
    documents: documentSelectors.documents(state),
    getVehicleError: errorSelectors.getVehicleError(state),
    setVehicleEnableError: errorSelectors.setVehicleEnableError(state),
});

const mapDispatchToProps = {
    clearError: errorActions.clearError,
    getVehicle: vehicleActions.getVehicle,
    setEnable: vehicleActions.setEnable,
};

export default connect(mapStateToProps, mapDispatchToProps)(VehicleViewPageWithStyleAndTranslation);
